// One progress bar. A job's progress lives on its workflow bar: the steps it has been through,
// with two of them filling as the shop works instead of flipping from not done to done:
//   - Cards pulled fills by the standard hours of the cards that have been pulled
//   - Build fills by the standard hours of the cards that have been closed
// both against the total standard hours of the job's cards. Actual hours are shown beside the
// standard. The job's one overall figure is its steps, with those two counted by how far
// along they are. There is no second bar anywhere.

import { Rat } from './rational.ts';
import { STEP } from './workflow.ts';

export interface ProgressCard {
  hours_standard: number | string | null;
  hours_actual?: number | string | null;
  /** Someone has pulled it (or it has been closed). */
  pulled: boolean;
  closed: boolean;
}

export interface CardProgress {
  cards: number;
  /** Decimal text, 2 places. */
  total_hours: string;
  pulled_hours: string;
  closed_hours: string;
  /** Actual hours logged on the closed cards, against their standard hours (closed_hours). */
  actual_hours: string;
  /** Whole percent, or null when the job has no cards with standard hours yet. */
  pulled_pct: number | null;
  closed_pct: number | null;
}

const HUNDRED = Rat.of(100n);
const num = (v: number | string | null | undefined) => Rat.tryParse(v ?? 0) ?? Rat.ZERO;
/** A share as a whole percent. It only reads 100 when it is all of it, and only 0 when it is none. */
function pct(part: Rat, whole: Rat): number | null {
  if (whole.sign() <= 0) return null;
  if (part.cmp(whole) >= 0) return 100;
  if (part.sign() <= 0) return 0;
  const p = Number(part.div(whole).mul(HUNDRED).round());
  return Math.min(99, Math.max(1, p));
}

export function cardProgress(cards: ProgressCard[]): CardProgress {
  let total = Rat.ZERO;
  let pulled = Rat.ZERO;
  let closed = Rat.ZERO;
  let actual = Rat.ZERO;
  for (const c of cards) {
    const h = num(c.hours_standard);
    total = total.add(h);
    if (c.pulled || c.closed) pulled = pulled.add(h);
    if (c.closed) {
      closed = closed.add(h);
      actual = actual.add(num(c.hours_actual));
    }
  }
  return {
    cards: cards.length,
    total_hours: total.toFixed(2),
    pulled_hours: pulled.toFixed(2),
    closed_hours: closed.toFixed(2),
    actual_hours: actual.toFixed(2),
    pulled_pct: pct(pulled, total),
    closed_pct: pct(closed, total),
  };
}

export interface ProgressStep {
  key: string;
  /** The job has an actual date for this step. */
  done: boolean;
}

export interface JobProgress {
  /** Whole percent across the job's steps. */
  overall_pct: number;
  steps_done: number;
  steps_total: number;
  /** step key → how full that step is, for the steps that fill. Absent while there are no cards. */
  fill: Record<string, number>;
  cards: CardProgress;
}

/**
 * The job's progress across the steps it goes through (already narrowed to its design path).
 * A step counts as done when it has an actual date. The two filling steps count by their
 * fraction once the job has cards, so the overall figure moves as cards are pulled and closed.
 */
export function jobProgress(steps: ProgressStep[], cards: ProgressCard[]): JobProgress {
  const cp = cardProgress(cards);
  const fill: Record<string, number> = {};
  if (cp.pulled_pct !== null) fill[STEP.cardsPulled] = cp.pulled_pct;
  if (cp.closed_pct !== null) fill[STEP.build] = cp.closed_pct;
  let score = Rat.ZERO;
  let done = 0;
  for (const s of steps) {
    const f = fill[s.key];
    if (f !== undefined) {
      // a filling step is as done as its cards say, whatever date it carries
      score = score.add(Rat.of(BigInt(f), 100n));
      if (f === 100) done++;
    } else if (s.done) {
      score = score.add(Rat.ONE);
      done++;
    }
  }
  const overall = steps.length === 0 ? 0 : Number(score.div(Rat.of(BigInt(steps.length))).mul(HUNDRED).round());
  return { overall_pct: Math.min(100, overall), steps_done: done, steps_total: steps.length, fill, cards: cp };
}
